<template>
  <div
    v-if="confirming"
    class="flex items-center gap-2 bg-red-50"
    :class="rowClass"
    role="group"
    :aria-label="`Delete ${title}`"
    @keydown.esc.stop="cancelDelete"
  >
    <p class="min-w-0 flex-1 truncate text-sm text-gray-700" aria-live="polite">
      {{
        deleteMutation.isError ? "Couldn't delete this chat" : "Delete chat?"
      }}
    </p>
    <button
      ref="cancelRef"
      class="rounded-md px-2 py-1 text-xs font-medium text-gray-600 hover:bg-white disabled:opacity-50"
      :disabled="deleteMutation.isPending"
      @click="cancelDelete"
    >
      Cancel
    </button>
    <button
      class="rounded-md bg-red-600 px-2 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
      :disabled="deleteMutation.isPending"
      @click="confirmDelete"
    >
      {{ deleteMutation.isError ? "Retry" : "Delete" }}
    </button>
  </div>
  <div v-else class="group relative flex items-center">
    <button
      class="flex w-full min-w-0 items-center text-left transition group-focus-within:pr-9 group-hover:pr-9"
      :class="[rowClass, buttonClass]"
      @click="emit('select', sessionId)"
    >
      <slot />
    </button>
    <button
      ref="trashRef"
      v-tooltip="'Delete chat'"
      class="absolute right-2 rounded-md bg-white p-1 text-gray-400 opacity-0 shadow-sm transition group-hover:opacity-100 hover:text-red-600 focus:opacity-100"
      :aria-label="`Delete ${title}`"
      @click="startDelete"
    >
      <Icon name="trash-alt" class="size-3.5" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { nextTick, ref } from "vue";
import Icon from "@/components/Core/Icon.vue";
import { useDeleteAISessionMutation } from "@/services/ai_agent";
import { useAiChatSession } from "@/composables/useAiChatSession";

const props = defineProps({
  sessionId: { type: Number, required: true },
  title: { type: String, default: "" },
  // Shared padding/rounding so the confirm state occupies the row's footprint.
  rowClass: { type: String, default: "" },
  buttonClass: { type: String, default: "" },
});

const emit = defineEmits({
  select: (id: number) => typeof id === "number",
});

const { sessionId: activeSessionId, restart } = useAiChatSession();
const deleteMutation = useDeleteAISessionMutation();
const confirming = ref(false);
const cancelRef = ref<HTMLButtonElement | null>(null);
const trashRef = ref<HTMLButtonElement | null>(null);

function startDelete(): void {
  deleteMutation.reset();
  confirming.value = true;
  void nextTick(() => cancelRef.value?.focus());
}

function cancelDelete(): void {
  // Esc reaches this too; once the DELETE is sent, cancelling can't stop it.
  if (deleteMutation.isPending) return;
  confirming.value = false;
  // The trash button is re-created on the way back; without this, focus
  // falls to <body> and keyboard users lose their place in the list.
  void nextTick(() => trashRef.value?.focus());
}

// mutateAsync, not mutate's per-call onSuccess: the hook's cache update drops
// this row from the list, and an unmounted observer skips per-call callbacks,
// so the open chat would never be reset.
async function confirmDelete(): Promise<void> {
  const sessionId = props.sessionId;
  try {
    await deleteMutation.mutateAsync(sessionId);
  } catch {
    return;
  }
  if (activeSessionId.value === sessionId) restart();
}
</script>
